import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { VYOMRA_LOGO_URL, PLATFORM_NAME } from "@/lib/brand";
import {
  GraduationCap, Search, BookOpen, Play, ChevronRight, CheckCircle2,
  LayoutDashboard, Users, ClipboardList, BedDouble, Ambulance,
  CalendarClock, FlaskConical, Scissors, Stethoscope, Wallet, Pill,
  MessageCircle, Shield, FileText, Files, ScrollText, Settings as SettingsIcon,
  Printer, Receipt, IndianRupee, Sparkles, Clock, AlertTriangle
} from "lucide-react";

/*
 * The Vyomra HOS · Training Hub
 * A single-page onboarding manual that walks a fresher through every core
 * workflow.  Each module is self-contained: an intro, a step-by-step
 * cheatsheet, and a "Try it" button that opens the real screen.
 */

const CATEGORY_COLOR = {
  overview: "bg-blue-50 text-blue-900 border-blue-200",
  clinical: "bg-teal-50 text-teal-900 border-teal-200",
  finance:  "bg-emerald-50 text-emerald-900 border-emerald-200",
  admin:    "bg-slate-100 text-slate-800 border-slate-300",
  power:    "bg-amber-50 text-amber-900 border-amber-200",
};

const MODULES = [
  // ─────────── Overview ───────────
  {
    id: "dashboard",
    title: "The Dashboard",
    category: "overview",
    icon: LayoutDashboard,
    minutes: 3,
    to: "/",
    tag: "Home",
    summary:
      "Your daily control-panel. The moment you log in, this is where you see how the hospital is doing today — how many patients came, how much money was collected, which beds are free.",
    concepts: [
      { term: "Date filter (Today / Week / Month)",
        detail: "Every KPI on this page respects the date filter at the top-left. Start with 'Today', switch to 'This Week' or 'Last 30 Days' for a bigger picture." },
      { term: "KPI tiles",
        detail: "OPD count, IPD count, Emergency count, Discharges, Total Collection, Advance, Refund, Bed Occupancy. Numbers refresh live as staff work." },
      { term: "Weekly Trend chart",
        detail: "Shows how OPD, IPD and Revenue moved over the last 7 days. Spot a slow day or a revenue dip instantly." },
      { term: "Quick Actions",
        detail: "One-tap buttons: New Patient · New OPD · New IPD · Emergency · Billing · OT / Surgery — skip 4 clicks and jump straight in." },
    ],
    steps: [
      "Change the date filter to 'This Month' — every tile updates.",
      "Hover the Weekly Trend chart to see day-wise OPD, IPD and revenue.",
      "Click any KPI tile (e.g. OPD) — you'll be taken to the full list.",
      "Use Quick Actions → 'New Patient' to register a walk-in visitor in seconds.",
    ],
    tips: [
      "If Bed Occupancy is above 90 %, warn the wards before an admission.",
      "The 'Refund' tile going up on a random day often signals a billing error worth checking.",
    ],
  },

  // ─────────── Patients ───────────
  {
    id: "patients",
    title: "Patient Register",
    category: "clinical",
    icon: Users,
    minutes: 4,
    to: "/patients",
    tag: "UHID",
    summary:
      "The single source of truth for every person who ever walked into the hospital. A patient exists here once; every visit (OPD, IPD, Emergency) links back to this record via a unique UHID.",
    concepts: [
      { term: "UHID",
        detail: "The permanent patient number (e.g. DH2405/000451). It is auto-generated on first registration and never reused." },
      { term: "Search",
        detail: "Type name, mobile, UHID or city in the search bar — results filter live." },
      { term: "Edit (pencil icon)",
        detail: "Every logged-in user can correct name / age / mobile / address. Reception uses this to fix typos discovered later." },
      { term: "Delete (red trash icon)",
        detail: "Only admin + super_admin see this. Blocks deletion if the patient has OPD / IPD / bill records — Super Admin can force a cascade delete." },
    ],
    steps: [
      "Click '+ New Patient' (top-right or Dashboard Quick Actions).",
      "Fill name · age · mobile (mandatory). Address, city, blood group and PMJAY are optional but recommended.",
      "Save — the system prints the UHID card and returns to the list.",
      "To edit later, find the row, click the pencil icon, change what you need, Save.",
    ],
    tips: [
      "Always search by mobile first — it catches 90 % of returning patients.",
      "Never create a duplicate. If unsure, click any matching row to see the full profile.",
      "Import a whole legacy register from Settings → Patient Register · Bulk Import.",
    ],
  },

  // ─────────── OPD ───────────
  {
    id: "opd",
    title: "OPD Visit · Consultation",
    category: "clinical",
    icon: ClipboardList,
    minutes: 6,
    to: "/opd",
    tag: "OPDxxxxx",
    summary:
      "Every doctor visit for an already-registered patient. Captures vitals, chief complaint, diagnosis, prescription, investigations and follow-up in one place. Auto-saves a draft every 30 seconds.",
    concepts: [
      { term: "OPD Number",
        detail: "Auto-generated as OPD00001, OPD00002, …. Different from the UHID — the UHID belongs to the person, the OPD number belongs to that day's visit." },
      { term: "Vitals",
        detail: "BP, Pulse, Temp, SpO₂, Weight, Height. Enter what's measured; leave the rest blank." },
      { term: "Live Rx preview",
        detail: "The right pane shows a live A5 preview of the prescription as you type — no need to click 'Print Preview' every time." },
      { term: "Follow-up quick picks",
        detail: "One-tap chips: 1 Day · 3 Days · 1 Week · 15 Days · 1 Month. Or open the calendar for a custom date." },
      { term: "Procedure Notes",
        detail: "A dedicated notepad next to Procedures — for stitch counts, dressing details, wound size etc. Prints on the Rx too." },
    ],
    steps: [
      "From Dashboard click '+ OPD' or type patient name in the search bar and pick 'Start OPD'.",
      "Select the consulting doctor from the dropdown.",
      "Record vitals (any that are relevant).",
      "Write chief complaint and provisional diagnosis.",
      "Add medicines — for each, dose (1-0-1, 0-0-1, SOS) auto-syncs the morning/afternoon/evening slot.",
      "Tick investigations or procedures as needed.",
      "Set Follow-up (chip or calendar).",
      "Print — the tab auto-closes after the print dialog is dismissed.",
    ],
    tips: [
      "Type 'SOS' in dose to auto-fill 0-0-0 (as-needed).",
      "Draft auto-saves every 30 seconds — the browser can crash and you won't lose the note.",
      "Click 'Preview Rx' at any time to see the full one-page prescription in a modal.",
    ],
  },

  // ─────────── IPD ───────────
  {
    id: "ipd",
    title: "IPD · Admissions",
    category: "clinical",
    icon: BedDouble,
    minutes: 6,
    to: "/ipd",
    tag: "IPDxxxxx",
    summary:
      "For every patient admitted to a bed. Handles bed assignment, daily nursing notes, real-time bill accumulation, advances, discharge summary and the final bill in one long-running record.",
    concepts: [
      { term: "Admission ↔ Discharge lifecycle",
        detail: "An IPD is 'Active' the day the patient is admitted and turns 'Discharged' when the doctor completes the discharge summary." },
      { term: "Charges table",
        detail: "Room rent, consultation, procedures, medicines, investigations — every charge is a bill_item row. You can edit the amount for a discount (see Editable Prices module)." },
      { term: "Advances vs Payments",
        detail: "Advance: money taken before treatment. Payment: money settled against a bill. The dashboard adds both to Total Collection but treats an unadjusted advance as a liability." },
      { term: "Discharge Summary",
        detail: "One-click template — captures condition, diagnoses, procedures done, meds at discharge, warning signs, follow-up. Prints on hospital letterhead." },
    ],
    steps: [
      "Click '+ New IPD' → pick the patient → select ward / bed.",
      "Save — the bed is now marked occupied on the Bed Management screen.",
      "Every day, add charges (room, IV, medicine, investigation).",
      "Take an advance whenever the family pays — record mode (Cash · UPI · Card).",
      "When it's time to discharge, click 'Discharge' → fill the summary → Save.",
      "Click 'Print Bill' → 'Final Bill A4' for the itemised statement.",
    ],
    tips: [
      "Bed availability is shown live in Bed Management — use it before admitting.",
      "You can edit any charge's amount on the fly (Edit ₹ button). Every override is audited.",
      "The IPD bill hides the 'Patient / Attendant Signature' line by hospital policy.",
    ],
  },

  // ─────────── Walk-in ───────────
  {
    id: "walkin",
    title: "Walk-in · Investigation Only",
    category: "clinical",
    icon: Receipt,
    minutes: 4,
    to: "/walkin-investigations",
    tag: "No doctor",
    summary:
      "For patients who came only for an X-Ray / ECG / USG / stitch removal / dressing — no doctor consult needed. Fastest possible flow: patient details + investigations + payment + 58 mm slip.",
    concepts: [
      { term: "Existing vs New patient",
        detail: "Search UHID / mobile / name first. Only fill the new-patient form when no match exists." },
      { term: "Per-line price override",
        detail: "Each investigation shows Qty and Rate as editable inputs. Change the rate to apply a discount (e.g. staff / senior citizen)." },
      { term: "58 mm slip",
        detail: "Auto-opens after Save — perfect for the thermal printer at reception. The tab closes itself after printing." },
      { term: "Reference Doctor",
        detail: "Optional field for the doctor who referred the patient (from outside)." },
    ],
    steps: [
      "Open Walk-in / Reports from the sidebar.",
      "Search patient — pick if found, otherwise fill the mini form.",
      "In the right pane, search 'X-RAY' / 'ECG' / 'USG' etc. Click each to add.",
      "Adjust Qty and Rate on each line for discounts.",
      "Click 'Save & Print 58mm Slip'.",
    ],
    tips: [
      "The slip is a receipt AND an investigation-request in one — the tech can start straight away.",
      "For post-op stitch removal, mention it in the 'Notes / Referral' field so records are audit-clean.",
    ],
  },

  // ─────────── Editable Prices ───────────
  {
    id: "prices",
    title: "Editable Prices · Discounts",
    category: "power",
    icon: IndianRupee,
    minutes: 3,
    to: "/opd",
    tag: "Any bill",
    summary:
      "Every price on the platform is editable. Reception can honour a discount without asking admin to change the master rate — the override stays on that one bill only, and is audited.",
    concepts: [
      { term: "Master rate vs Bill item rate",
        detail: "The master (Investigation Master, Procedure Charges) sets the default. When a charge is created, that default is copied into the bill_item. From that moment the two are independent." },
      { term: "Reason (audit)",
        detail: "Every price change asks for a reason. It's saved in charge_overrides and shows up in Audit Logs — so senior-citizen discounts can be reviewed later." },
    ],
    steps: [
      "Open the SelectiveBillDialog (Billing → 'Print Custom Receipt' → pick an OPD).",
      "Find the row → click the small pencil icon.",
      "Enter the new amount and a short reason (e.g. 'PMJAY').",
      "Click ✓ Save — total refreshes instantly.",
      "On IPD, the same works via the 'Edit ₹' button in the Billing tab.",
    ],
    tips: [
      "Never zero-out a charge — set it to ₹1 with reason 'Waived' so the audit trail stays clean.",
      "Always add a reason. 'Discount' alone is not enough — write 'Senior citizen 65+' or 'Staff family'.",
    ],
  },

  // ─────────── Billing ───────────
  {
    id: "billing",
    title: "Billing · Collections",
    category: "finance",
    icon: Wallet,
    minutes: 5,
    to: "/billing",
    tag: "Money in",
    summary:
      "Central register of every rupee collected. Take payments, issue receipts, offer credit / debit, print 58 mm or A4 bills, and see who owes what — all here.",
    concepts: [
      { term: "Modes",
        detail: "Cash · UPI · Card · Netbanking · Cheque · PMJAY. The Dashboard Payment Modes tile groups collection by mode automatically." },
      { term: "Credit ledger",
        detail: "When a patient is billed but not paid (Government / Insurance), it goes into Credit Ledger. Track outstandings and settle later." },
      { term: "Print formats",
        detail: "58 mm (thermal) · 80 mm · A4. Pick 58 mm for quick receipts, A4 for insurance / employer reimbursement." },
    ],
    steps: [
      "From an OPD or IPD, click 'Add Payment' → enter amount, mode → Save.",
      "For a partial bill (only some items), open 'Print Custom Receipt' and tick what to include.",
      "Use Credit Ledger to see all outstanding amounts by patient / party.",
    ],
    tips: [
      "Reconcile at end-of-day using Reports → Daily Collection.",
      "Cheques → mark 'Pending' until the cheque clears, then flip to 'Received'.",
    ],
  },

  // ─────────── Rx ───────────
  {
    id: "rx",
    title: "Prescription (Rx) Printing",
    category: "clinical",
    icon: FileText,
    minutes: 3,
    to: "/opd",
    tag: "One page",
    summary:
      "Every prescription prints on one A4 page with the hospital letterhead, patient block, vitals, medicines and a live signature area.",
    concepts: [
      { term: "Live preview",
        detail: "The Rx tab in OPD Consultation shows the exact printed layout, updating as you type." },
      { term: "Doctor signature toggle",
        detail: "Settings → Print Preferences → 'Show doctor signature on Rx'. OFF by default because most doctors use a rubber stamp." },
      { term: "Auto-close",
        detail: "The print tab closes itself once you hit Print or Cancel — no lingering tabs." },
    ],
    steps: [
      "Complete the OPD consult → click 'Print'.",
      "A new tab opens with the print dialog already up.",
      "Click Print (or send to PDF).",
      "The tab closes on its own.",
    ],
    tips: [
      "One page only. If content overflows, shrink long descriptions.",
      "Turn the signature toggle ON only for doctors who upload a real signature PNG.",
    ],
  },

  // ─────────── Kiosk ───────────
  {
    id: "kiosk",
    title: "Self-Service Kiosk",
    category: "clinical",
    icon: Sparkles,
    minutes: 4,
    to: "/kiosk",
    tag: "Reception",
    summary:
      "A large-tile self-registration screen for the front desk tablet. New patients register themselves; returning patients pick their UHID; the receipt prints instantly.",
    concepts: [
      { term: "Operator login",
        detail: "The person on duty signs in once (email + password). Every registration made on that tablet is attributed to them." },
      { term: "OPD / walk-in modes",
        detail: "New OPD (consult) vs Walk-in (only reports). The Kiosk lets the visitor pick before entering details." },
    ],
    steps: [
      "Open Kiosk on the reception tablet (Chrome, full-screen, F11).",
      "Operator signs in with their Vyomra email + password.",
      "Tap 'Start' — visitor selects new / returning → OPD / walk-in.",
      "Visitor fills the big-touch form → the 58 mm slip prints automatically.",
    ],
    tips: [
      "Keep the tablet plugged in and the thermal printer paired via Bluetooth.",
      "The Kiosk is protected by the same auth as the main app — no extra password to remember.",
    ],
  },

  // ─────────── Settings ───────────
  {
    id: "settings",
    title: "Settings · Preferences",
    category: "admin",
    icon: SettingsIcon,
    minutes: 4,
    to: "/settings",
    tag: "Admin",
    summary:
      "The place where admin tweaks how everything prints and imports. Nothing here breaks a workflow — it just polishes it.",
    concepts: [
      { term: "Print Preferences",
        detail: "Show / hide the doctor signature on Rx. Show / hide the patient signature line on OPD bill (IPD hides it by policy)." },
      { term: "Patient Register · Bulk Import",
        detail: "Upload an XLS from your old software — dedupes by (name, mobile) and auto-assigns UHIDs." },
      { term: "Emergency & Procedure Charges Master",
        detail: "The default rates that flow into every new bill_item." },
    ],
    steps: [
      "Open Settings from the sidebar.",
      "Toggle the print preferences you want — click 'Save Print Preferences'.",
      "For bulk imports, click 'Upload Patient Register (XLS/XLSX)'.",
    ],
    tips: [
      "Adjust the print toggles once during onboarding — you won't touch them again for months.",
      "Bulk-import in off-hours; a 13,000-row file takes ~30 seconds to insert.",
    ],
  },

  // ─────────── Reports ───────────
  {
    id: "reports",
    title: "Reports · Analytics",
    category: "admin",
    icon: FileText,
    minutes: 3,
    to: "/reports",
    tag: "MIS",
    summary:
      "Daily / weekly / monthly reports on OPD, IPD, revenue, doctor commissions, department load. Everything is exportable.",
    concepts: [
      { term: "Filter by date + department",
        detail: "Any report can be re-run for a custom period. Save common filters as presets." },
      { term: "Export",
        detail: "PDF (print-ready) or Excel (raw data)." },
    ],
    steps: [
      "Reports → pick the report → set date range → Run.",
      "Click Export → PDF or Excel.",
    ],
    tips: [
      "The Doctor Commission report is the fastest way to prep monthly payouts.",
    ],
  },
];

const CATEGORIES = [
  { id: "all",       label: "All modules", icon: BookOpen },
  { id: "overview",  label: "Overview",    icon: LayoutDashboard },
  { id: "clinical",  label: "Clinical",    icon: Stethoscope },
  { id: "finance",   label: "Finance",     icon: Wallet },
  { id: "admin",     label: "Admin",       icon: SettingsIcon },
  { id: "power",     label: "Power moves", icon: Sparkles },
];

export default function Training() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [openId, setOpenId] = useState(MODULES[0].id);
  // Simple checkpoint tracker persisted in localStorage — new hires can watch
  // their progress fill the bar as they finish each module.
  const [done, setDone] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("vy_training_done") || "[]")); }
    catch { return new Set(); }
  });

  const markDone = (id) => {
    const next = new Set(done);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setDone(next);
    localStorage.setItem("vy_training_done", JSON.stringify([...next]));
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return MODULES.filter((m) => {
      if (cat !== "all" && m.category !== cat) return false;
      if (!t) return true;
      const hay = (m.title + " " + m.summary + " " + m.concepts.map((c) => c.term + " " + c.detail).join(" ")).toLowerCase();
      return hay.includes(t);
    });
  }, [q, cat]);

  const totalMinutes = MODULES.reduce((s, m) => s + m.minutes, 0);
  const pct = Math.round((done.size / MODULES.length) * 100);

  return (
    <div className="space-y-6" data-testid="training-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 text-white p-6 rounded-sm relative overflow-hidden">
        <div className="absolute -right-6 -bottom-6 opacity-10">
          <img src={VYOMRA_LOGO_URL} alt="" className="h-56" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-2 text-blue-200 text-xs tracking-[0.2em] uppercase mb-1">
            <GraduationCap className="w-4 h-4" /> {PLATFORM_NAME} · Training Hub
          </div>
          <h1 className="font-display text-3xl font-semibold">Learn {PLATFORM_NAME} in under an hour.</h1>
          <p className="text-blue-100 text-sm mt-2 max-w-2xl">
            A structured onboarding path for every new joiner — reception, doctors, nurses and admin.
            Each module is a short read (2–6 min), a step-by-step cheatsheet and a "Try it" button that opens the real screen.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <Badge className="bg-white/15 text-white border-white/30 hover:bg-white/25" data-testid="training-hero-modules">{MODULES.length} modules</Badge>
            <Badge className="bg-white/15 text-white border-white/30 hover:bg-white/25">{totalMinutes} minutes total</Badge>
            <Badge className="bg-white/15 text-white border-white/30 hover:bg-white/25">Self-paced</Badge>
          </div>
          <div className="mt-4 max-w-md">
            <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-blue-200 mb-1">
              <span>Your progress</span><span>{done.size}/{MODULES.length} · {pct}%</span>
            </div>
            <div className="h-1.5 bg-white/15 rounded-full overflow-hidden">
              <div className="h-full bg-teal-300 transition-all" style={{ width: `${pct}%` }} data-testid="training-progress-bar"/>
            </div>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a topic (e.g. 'discount', 'UHID', 'walk-in')" className="pl-7 h-9 w-72" data-testid="training-search"/>
        </div>
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = cat === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`training-cat-${c.id}`}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] uppercase tracking-wide border rounded-sm transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                <Icon className="w-3 h-3"/> {c.label}
              </button>
            );
          })}
        </div>
        {done.size > 0 && (
          <button
            onClick={() => { setDone(new Set()); localStorage.removeItem("vy_training_done"); }}
            data-testid="training-reset"
            className="ml-auto text-[11px] text-red-600 hover:underline">
            Reset my progress
          </button>
        )}
      </div>

      {/* Module grid */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-slate-500">No modules match your search.</Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Sidebar list */}
          <div className="lg:col-span-1 space-y-2">
            {filtered.map((m) => {
              const Icon = m.icon;
              const isDone = done.has(m.id);
              const isOpen = openId === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setOpenId(m.id)}
                  data-testid={`training-mod-${m.id}`}
                  className={`w-full text-left border p-3 rounded-sm transition flex items-start gap-3 ${isOpen ? "bg-blue-50 border-blue-300 ring-1 ring-blue-200" : "bg-white hover:bg-slate-50 border-slate-200"}`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-sm border ${CATEGORY_COLOR[m.category]}`}>
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate">{m.title}</span>
                      {isDone && <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 shrink-0"/>}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{m.tag} · {m.minutes} min</div>
                  </div>
                  <ChevronRight className={`w-3.5 h-3.5 shrink-0 mt-1 transition ${isOpen ? "rotate-90 text-blue-700" : "text-slate-400"}`}/>
                </button>
              );
            })}
          </div>

          {/* Active module detail */}
          <div className="lg:col-span-2">
            {filtered.map((m) => {
              if (m.id !== openId) return null;
              const Icon = m.icon;
              const isDone = done.has(m.id);
              return (
                <div key={m.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`training-detail-${m.id}`}>
                  <div className={`px-5 py-4 border-b ${CATEGORY_COLOR[m.category]}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/70 border border-white/40 rounded-sm">
                        <Icon className="w-5 h-5"/>
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{m.category}</div>
                        <h2 className="font-display text-xl font-semibold">{m.title}</h2>
                        <div className="text-xs opacity-70 mt-0.5"><Clock className="w-3 h-3 inline mr-1"/>{m.minutes} min read · {m.tag}</div>
                      </div>
                      <button
                        onClick={() => markDone(m.id)}
                        data-testid={`training-mark-${m.id}`}
                        className={`text-xs border px-3 py-1.5 rounded-sm transition ${isDone ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-700 hover:bg-slate-50 border-slate-300"}`}
                      >
                        {isDone ? "✓ Completed" : "Mark as done"}
                      </button>
                    </div>
                  </div>

                  <div className="p-5 space-y-5">
                    <p className="text-slate-700 leading-relaxed">{m.summary}</p>

                    {/* Concepts */}
                    <div>
                      <div className="label-tiny mb-2 flex items-center gap-2"><BookOpen className="w-3.5 h-3.5"/> Key concepts</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {m.concepts.map((c, i) => (
                          <div key={i} className="border p-3 rounded-sm bg-slate-50/50">
                            <div className="text-sm font-semibold text-slate-900">{c.term}</div>
                            <div className="text-xs text-slate-600 mt-1 leading-relaxed">{c.detail}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Steps */}
                    <div>
                      <div className="label-tiny mb-2 flex items-center gap-2"><Play className="w-3.5 h-3.5"/> Step-by-step</div>
                      <ol className="border rounded-sm divide-y bg-white">
                        {m.steps.map((s, i) => (
                          <li key={i} className="flex items-start gap-3 px-3 py-2 text-sm">
                            <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-[11px] font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                            <span className="flex-1 text-slate-800 leading-relaxed">{s}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* Tips */}
                    {m.tips?.length > 0 && (
                      <div className="border-l-4 border-amber-400 bg-amber-50 px-4 py-3">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-900 flex items-center gap-1 mb-1">
                          <AlertTriangle className="w-3 h-3"/> Pro tips
                        </div>
                        <ul className="list-disc pl-5 text-xs text-amber-900 space-y-1">
                          {m.tips.map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                      </div>
                    )}

                    {/* CTA */}
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Link to={m.to}>
                        <Button className="bg-blue-700 hover:bg-blue-800 rounded-sm" data-testid={`training-try-${m.id}`}>
                          <Play className="w-4 h-4 mr-1"/> Try it now
                        </Button>
                      </Link>
                      <button
                        onClick={() => {
                          const idx = MODULES.findIndex((x) => x.id === m.id);
                          const next = MODULES[idx + 1];
                          if (next) setOpenId(next.id);
                        }}
                        data-testid={`training-next-${m.id}`}
                        className="text-xs px-3 py-2 border rounded-sm hover:bg-slate-50"
                      >
                        Next module <ChevronRight className="inline w-3 h-3"/>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer help */}
      <div className="bg-slate-50 border p-4 flex items-center gap-3 text-sm">
        <Sparkles className="w-4 h-4 text-blue-700"/>
        <div>
          Tip for the trainer: pair a fresher with a senior for the first 3 days.  Have them finish{" "}
          <b>Dashboard → Patients → OPD → Billing → Walk-in</b> on Day 1, then IPD + Discharge on Day 2, and everything else on Day 3.
        </div>
      </div>
    </div>
  );
}
